import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { PhoneAndroid as PhoneIcon, Email as EmailIcon, Sms as SmsIcon, Send as SendIcon, VerifiedUser as VerifiedIcon, Password as PasswordIcon, Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { fieldSx, goldButtonSx, fadeUp, slideIn } from "./signinStyles";
import { SwapTabs, SwapPanes } from "./SwapUI";

// The Google button has to be the one that gets clicked (it hands back a verified ID token), so it sits
// invisibly on top of a button drawn to match the page.
function GoogleButton({ busy, onSuccess, onError }) {
  const box = useRef(null);
  const [width, setWidth] = useState(310);

  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const update = () => setWidth(Math.min(400, Math.max(200, Math.round(el.offsetWidth))));
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box
      ref={box}
      sx={{
        position: "relative",
        height: 46,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: "1px solid rgba(200,160,84,0.28)",
        boxShadow: "0 2px 8px rgba(60,40,10,0.06)",
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.35s ease, border-color 0.35s ease, transform 0.35s ease, background-color 0.35s ease",
        "&:hover": { bgcolor: "#fffdf8", borderColor: "rgba(200,160,84,0.6)", boxShadow: "0 8px 22px rgba(200,160,84,0.22)", transform: "translateY(-1px)" },
        "&:active": { transform: "translateY(0)" },
        opacity: busy ? 0.65 : 1,
      }}
    >
      {/* what the visitor sees */}
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25, pointerEvents: "none" }}>
        {busy ? (
          <CircularProgress size={18} sx={{ color: "#C8A054" }} />
        ) : (
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
        )}
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#3c3a35" }}>
          {busy ? "Signing you in…" : "Continue with Google"}
        </Typography>
      </Box>

      {/* the real Google button, invisible, covering the whole area */}
      <Box sx={{ position: "absolute", inset: 0, opacity: 0.001, display: "flex", justifyContent: "center", "& iframe, & div": { cursor: "pointer" } }}>
        <GoogleLogin onSuccess={onSuccess} onError={onError} text="continue_with" shape="rectangular" width={String(width)} />
      </Box>
    </Box>
  );
}

// The three ways a retail customer can sign in: Google, mobile number with an OTP, or email and password.
// `children` is the existing email + password form; onSignedIn receives { token, customer }.
function CustomerSignInPanel({ onSignedIn, onError, children }) {
  const [method, setMethod] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const fail = (message) => { if (onError) onError(message); };

  const sendOtp = async () => {
    setFieldError("");
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setFieldError("Enter a valid 10-digit mobile number");
      return;
    }
    fail("");
    setBusy(true);
    try {
      await postData("customer/web/mobile/send-otp", { mobile }, "", false, false);
      setOtpSent(true);
      setOtp("");
      setWait(30);
    } catch (e) {
      fail(e.message || "Could not send the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setFieldError("");
    if (!/^\d{6}$/.test(otp)) {
      setFieldError("Enter the 6-digit code");
      return;
    }
    fail("");
    setBusy(true);
    try {
      const res = await postData("customer/web/mobile/verify-otp", { mobile, code: otp }, "", false, false);
      if (res?.token) onSignedIn(res);
      else fail(res?.message || "Could not sign you in. Please try again.");
    } catch (e) {
      fail(e.message || "Could not verify the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = async (credentialResponse) => {
    fail("");
    setBusy(true);
    try {
      const res = await postData("customer/web/google-login", { credential: credentialResponse.credential }, "", false, false);
      if (res?.token) onSignedIn(res);
      else fail(res?.message || "Google sign-in failed. Please try again.");
    } catch (e) {
      fail(e.message || "Google sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {/* Google */}
      <GoogleButton busy={busy} onSuccess={googleSignIn} onError={() => fail("Google sign-in failed. Please try again.")} />

      <Divider sx={{ my: 1.75, color: "#a39a80", fontSize: "0.72rem", letterSpacing: "1px", textTransform: "uppercase", "&::before, &::after": { borderColor: "rgba(200,160,84,0.25)" } }}>or sign in with</Divider>

      <SwapTabs
        value={method}
        onChange={(v) => { setMethod(v); setFieldError(""); fail(""); }}
        options={[
          { value: "mobile", label: "Mobile", icon: <PhoneIcon sx={{ fontSize: 18 }} /> },
          { value: "email", label: "Email", icon: <EmailIcon sx={{ fontSize: 18 }} /> },
        ]}
      />

      <SwapPanes index={method === "mobile" ? 0 : 1}>
        {/* section 1: mobile number + OTP */}
        <Box>
        <Box key={otpSent ? "otp" : "number"} sx={{ animation: `${otpSent ? slideIn : fadeUp} 0.45s both` }}>
          {!otpSent ? (
            <>
              <TextField
                fullWidth
                placeholder="Mobile number"
                size="small"
                value={mobile}
                onChange={(e) => { setMobile(e.target.value.replace(/\D/g, "").slice(0, 10)); setFieldError(""); }}
                variant="outlined"
                margin="dense"
                autoFocus
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#C8A054", fontSize: 20, mr: 0.75 }} />+91</InputAdornment> }}
                error={!!fieldError}
                helperText={fieldError || "We will SMS you a 6-digit code. New here? We will create your account."}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
                sx={fieldSx}
              />
              <Button fullWidth variant="contained" onClick={sendOtp} disabled={busy} startIcon={busy ? null : <SmsIcon />} sx={{ ...goldButtonSx, mt: 0.75 }}>
                {busy ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Send OTP"}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Code sent to +91 {mobile}.{" "}
                <Link component="button" type="button" underline="hover" onClick={() => { setOtpSent(false); setOtp(""); setFieldError(""); fail(""); }} sx={{ color: "#8a6a24", fontWeight: 600, verticalAlign: "baseline", display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                  <EditIcon sx={{ fontSize: 15 }} />
                  Change
                </Link>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter 6-digit OTP"
                size="small"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setFieldError(""); }}
                variant="outlined"
                margin="dense"
                autoFocus
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><PasswordIcon sx={{ color: "#C8A054", fontSize: 20 }} /></InputAdornment> }}
                error={!!fieldError}
                helperText={fieldError}
                onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }}
                sx={fieldSx}
              />
              <Button fullWidth variant="contained" onClick={verifyOtp} disabled={busy} startIcon={busy ? null : <VerifiedIcon />} sx={{ ...goldButtonSx, mt: 0.75 }}>
                {busy ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Verify & Sign In"}
              </Button>
              <Typography variant="body2" align="center" sx={{ mt: 2, color: "text.secondary" }}>
                {wait > 0 ? (
                  `Resend code in ${wait}s`
                ) : (
                  <Link component="button" type="button" underline="hover" onClick={sendOtp} sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <RefreshIcon sx={{ fontSize: 17 }} />
                    Resend code
                  </Link>
                )}
              </Typography>
            </>
          )}
        </Box>
        </Box>

        {/* section 2: email and password */}
        <Box>{children}</Box>
      </SwapPanes>
    </Box>
  );
}

export default CustomerSignInPanel;
